import clsx from "clsx";
import { useFormikContext } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../../../components/Button";
import CustomInput from "../../../components/Input";
import { api } from "../../../helpers/api";

// Same rule as the form's Yup schema (no "+" aliases), so a verified email always passes it.
const EMAIL_PATTERN = /^[^\s+@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_SECONDS = 30;
const INVALID_EMAIL = "Please enter a valid email, like name@company.com.";
const LOCKED =
  "Too many attempts. Try again in 10 minutes, or use a different email.";
const GENERIC_ERROR = "Something went wrong. Please try again.";
const LINK_CLASS =
  "font-medium underline text-[var(--d-accent-deep)] hover:text-[var(--d-accent)] transition-colors";

enum EmailStatus {
  Idle = "idle",
  Sending = "sending",
  Sent = "sent",
  Verified = "verified",
}

enum OtpStatus {
  Idle = "idle",
  Checking = "checking",
  Wrong = "wrong",
  Expired = "expired",
  Locked = "locked",
}

// Mirrors SignupOtpErrorCode in autumn-backend (src/modules/auth/signup-otp.service.ts).
export enum OtpErrorCode {
  InvalidEmail = "invalid_email",
  AlreadyRegistered = "already_registered",
  WrongCode = "wrong_code",
  Expired = "expired",
  Locked = "locked",
  RateLimited = "rate_limited",
  SendFailed = "send_failed",
  EmailNotVerified = "email_not_verified",
}

interface OtpErrorBody {
  status?: number;
  code?: string;
  message?: string;
  attemptsLeft?: number;
  retryAfterSeconds?: number;
}

function errorBody(err: unknown): OtpErrorBody {
  const res = (err as { response?: { status?: number; data?: OtpErrorBody } })
    ?.response;
  return { ...res?.data, status: res?.status };
}

function sendErrorMessage(body: OtpErrorBody): React.ReactNode {
  switch (body.code) {
    case OtpErrorCode.InvalidEmail:
      return INVALID_EMAIL;
    case OtpErrorCode.AlreadyRegistered:
      return (
        <>
          Looks like this email is already registered! Please sign in{" "}
          <Link to="/login" className={LINK_CLASS}>
            HERE
          </Link>{" "}
          instead
        </>
      );
    case OtpErrorCode.Locked:
      return LOCKED;
    case OtpErrorCode.RateLimited:
    case OtpErrorCode.SendFailed:
      return body.message;
  }
  return body.status === 429
    ? "Too many requests. Please try again later."
    : GENERIC_ERROR;
}

const formatCountdown = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const focusById = (id: string) =>
  requestAnimationFrame(() => document.getElementById(id)?.focus());

/**
 * Email input with a server-verified one-time code. Calls `onVerified` with the
 * signed token /auth/register requires once the code checks out.
 */
export function EmailOtpField({
  onVerified,
}: {
  onVerified: (token: string) => void;
}) {
  const { values, setFieldValue } = useFormikContext<{ email: string }>();
  const email = values.email.trim();
  const [status, setStatus] = useState(EmailStatus.Idle);
  const [emailError, setEmailError] = useState<React.ReactNode>(null);
  const [otpStatus, setOtpStatus] = useState(OtpStatus.Idle);
  const [otpError, setOtpError] = useState<React.ReactNode>(null);
  const [codeLength, setCodeLength] = useState(4);
  const [digits, setDigits] = useState<string[]>([]);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [resendIn, setResendIn] = useState(0);
  const [resent, setResent] = useState(false);
  const boxRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Bumped whenever the step is abandoned (new send, Change, verified) so late replies are ignored.
  const stepRef = useRef(0);
  const resendingRef = useRef(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const focusBox = (i: number) =>
    requestAnimationFrame(() => boxRefs.current[i]?.focus());

  async function sendCode(isResend = false) {
    if (!EMAIL_PATTERN.test(email)) {
      setEmailError(INVALID_EMAIL);
      return;
    }
    if (isResend) {
      if (resendingRef.current) return; // a resend is already in flight
      resendingRef.current = true;
    }
    const step = isResend ? stepRef.current : ++stepRef.current;
    if (!isResend) {
      setStatus(EmailStatus.Sending);
      // Store the trimmed address: the form validates and submits Formik's value, not ours.
      setFieldValue("email", email);
    }
    setOtpError(null);
    try {
      const { data } = await api.sendSignupOtp(email);
      if (step !== stepRef.current) return;
      setCodeLength(data.codeLength);
      setDigits(Array(data.codeLength).fill(""));
      setStatus(EmailStatus.Sent);
      setOtpStatus(OtpStatus.Idle);
      setAttemptsLeft(3);
      setResendIn(RESEND_SECONDS);
      setResent(isResend);
      focusBox(0);
    } catch (err) {
      if (step !== stepRef.current) return;
      const body = errorBody(err);
      if (!isResend) {
        setStatus(EmailStatus.Idle);
        setEmailError(sendErrorMessage(body));
      } else if (body.code === OtpErrorCode.Locked) {
        setOtpStatus(OtpStatus.Locked);
      } else {
        if (body.retryAfterSeconds) setResendIn(body.retryAfterSeconds);
        setOtpError(sendErrorMessage(body));
      }
    } finally {
      if (isResend) resendingRef.current = false;
    }
  }

  async function verify(code: string) {
    const step = stepRef.current;
    setOtpStatus(OtpStatus.Checking);
    setOtpError(null);
    try {
      const { data } = await api.verifySignupOtp(email, code);
      if (step !== stepRef.current) return;
      stepRef.current++;
      setStatus(EmailStatus.Verified);
      onVerified(data.verificationToken);
      focusById("firstName");
    } catch (err) {
      if (step !== stepRef.current) return;
      const body = errorBody(err);
      if (
        body.code === OtpErrorCode.Expired ||
        body.code === OtpErrorCode.Locked
      ) {
        setOtpStatus(
          body.code === OtpErrorCode.Expired
            ? OtpStatus.Expired
            : OtpStatus.Locked,
        );
        return;
      }
      setDigits(Array(codeLength).fill(""));
      if (body.code === OtpErrorCode.WrongCode) {
        setAttemptsLeft(body.attemptsLeft ?? 0);
        setOtpStatus(OtpStatus.Wrong);
      } else {
        setOtpStatus(OtpStatus.Idle);
        setOtpError(GENERIC_ERROR);
      }
      focusBox(0);
    }
  }

  function changeEmail() {
    stepRef.current++;
    setStatus(EmailStatus.Idle);
    setOtpStatus(OtpStatus.Idle);
    setOtpError(null);
    setDigits([]);
    setResent(false);
    setResendIn(0);
    focusById("email");
  }

  function setDigit(i: number, raw: string) {
    let v = raw.replace(/\D/g, "");
    // Typing over a filled box yields two chars; keep the new one.
    if (v.length === 2 && digits[i]) {
      v = v.startsWith(digits[i]) ? v.slice(1) : v.slice(0, 1);
    }
    const next = [...digits];
    if (!v) next[i] = "";
    v.slice(0, codeLength - i)
      .split("")
      .forEach((d, k) => (next[i + k] = d));
    setDigits(next);
    setResent(false);
    setOtpError(null);
    if (otpStatus === OtpStatus.Wrong) setOtpStatus(OtpStatus.Idle);
    if (next.every(Boolean)) verify(next.join(""));
    else if (v) focusBox(Math.min(i + v.length, codeLength - 1));
  }

  function onBoxKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      setDigits((d) => d.map((x, k) => (k === i - 1 ? "" : x)));
      focusBox(i - 1);
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight" && i < codeLength - 1) {
      e.preventDefault();
      focusBox(i + 1);
    }
  }

  const locked = status !== EmailStatus.Idle;
  const verified = status === EmailStatus.Verified;
  const boxesDisabled = [
    OtpStatus.Checking,
    OtpStatus.Expired,
    OtpStatus.Locked,
  ].includes(otpStatus);

  return (
    <div className="flex flex-col">
      <label htmlFor="email" className="d-field-label">
        <span>Email</span>
      </label>
      <div className="flex items-stretch gap-2">
        <CustomInput
          id="email"
          name="email"
          type="email"
          placeholder="you@hotel.com"
          autoComplete="email"
          value={values.email}
          readOnly={locked}
          className="flex-1 min-w-0"
          inputClassName={clsx(emailError && "is-error")}
          // Inline because the kit's `[data-design] .d-input` outranks utility classes.
          style={
            locked
              ? {
                  background: "var(--d-tint-page)",
                  borderColor: verified ? "var(--d-pos-text)" : undefined,
                }
              : undefined
          }
          onChange={(e) => {
            setFieldValue("email", e.target.value);
            setEmailError(null);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            if (status === EmailStatus.Idle) sendCode();
          }}
        />
        {(status === EmailStatus.Idle || status === EmailStatus.Sending) && (
          <Button
            variant="secondary"
            size="default"
            className="shrink-0 whitespace-nowrap"
            disabled={
              !EMAIL_PATTERN.test(email) || status === EmailStatus.Sending
            }
            onClick={() => sendCode()}
          >
            {status === EmailStatus.Sending ? "Sending…" : "Verify"}
          </Button>
        )}
        {status === EmailStatus.Sent && (
          <Button
            variant="secondary"
            size="default"
            className="shrink-0"
            onClick={changeEmail}
          >
            Change
          </Button>
        )}
        {verified && (
          <span className="shrink-0 inline-flex items-center gap-1.5 px-3.5 rounded-[var(--d-r-md)] bg-[var(--d-pos-soft)] text-[13px] font-medium text-[var(--d-pos-text)]">
            ✓ Verified
          </span>
        )}
      </div>
      {emailError && <p className="d-field-error">{emailError}</p>}

      {status === EmailStatus.Sent && (
        <div
          className="mt-2.5 flex flex-col gap-3 rounded-[var(--d-r-lg)] border border-[var(--d-rule)] bg-[var(--d-surface)] p-4 text-[13px] leading-[1.45] text-[var(--d-ink)]"
          style={{ fontFamily: "var(--d-font-body)" }}
        >
          <p>
            Please enter the {codeLength}-digit code we sent to{" "}
            <b className="font-semibold">{email}</b>
          </p>
          <div className="flex items-center gap-2">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  boxRefs.current[i] = el;
                }}
                value={d}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                aria-label={`Digit ${i + 1}`}
                autoFocus={i === 0}
                disabled={boxesDisabled}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onBoxKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
                className={clsx(
                  "h-[54px] w-12 rounded-[var(--d-r-md)] border-[1.5px] p-0 text-center text-[22px] font-semibold text-[var(--d-ink)] outline-none transition-colors focus:border-[var(--d-accent)] disabled:cursor-not-allowed",
                  otpStatus === OtpStatus.Wrong
                    ? "border-[var(--d-neg-text)]"
                    : d
                      ? "border-[var(--d-ink)]"
                      : "border-[var(--d-accent-soft)]",
                  boxesDisabled
                    ? "bg-[var(--d-tint-page)]"
                    : "bg-[var(--d-surface)]",
                )}
              />
            ))}
            {otpStatus === OtpStatus.Checking && (
              <span className="ml-1.5 text-[var(--d-ink-muted)]">
                Checking…
              </span>
            )}
          </div>

          {otpStatus === OtpStatus.Wrong && (
            <p className="text-[var(--d-neg-text)]">
              That code isn't right. {attemptsLeft}{" "}
              {attemptsLeft === 1 ? "attempt" : "attempts"} left.
            </p>
          )}
          {otpStatus === OtpStatus.Expired && (
            <p className="text-[var(--d-neg-text)]">
              This code has expired.{" "}
              <button
                type="button"
                className={LINK_CLASS}
                onClick={() => sendCode(true)}
              >
                Send a new code
              </button>
            </p>
          )}
          {otpStatus === OtpStatus.Locked && (
            <p className="text-[var(--d-neg-text)]">
              Too many attempts. Try again in 10 minutes, or{" "}
              <button
                type="button"
                className={LINK_CLASS}
                onClick={changeEmail}
              >
                use a different email
              </button>
              .
            </p>
          )}
          {otpError && <p className="text-[var(--d-neg-text)]">{otpError}</p>}
          {resent && otpStatus === OtpStatus.Idle && (
            <p className="text-[var(--d-pos-text)]">New code sent.</p>
          )}
          {otpStatus !== OtpStatus.Expired &&
            otpStatus !== OtpStatus.Locked && (
              <p className="text-[var(--d-ink-muted)]">
                Didn't get it? Check spam, or{" "}
                {resendIn > 0 ? (
                  `resend in ${formatCountdown(resendIn)}`
                ) : (
                  <button
                    type="button"
                    className={LINK_CLASS}
                    disabled={otpStatus === OtpStatus.Checking}
                    onClick={() => sendCode(true)}
                  >
                    resend code
                  </button>
                )}
              </p>
            )}
        </div>
      )}
    </div>
  );
}
